import React, { useEffect, useMemo } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import {
  AccordionItem as Item,
  useAccordionProvider,
  ControlledAccordion,
} from '@szhsin/react-accordion';
import fascilitatorTrainingRoutes, {
  FACILITATOR_CERTIFICATE_ROUTE,
  flatFacilitatorRoutes,
} from './flow';
import { Navbar } from '@mantine/core';
import { isFacilitatorTrainingUnlockedForTesting } from '../../useFacilitatorStore';
import { FACILITATOR_NAV_BREAKPOINT } from './navConfig';
import {
  hasStartedProgramFromProgress,
  isAdminProgramCompleteFromProgress,
} from '../../useAdminProgressQuery';
import FacilitatorHeaderActions from './FacilitatorHeaderActions';
import { hasFullFacilitatorProgramAccessRole } from '../../../../utils/authRole';

const modStyles =
  'text-base text-left px-3 py-4 block w-full whitespace-normal break-words leading-snug';
const FACILITATOR_HOME_ROUTE = '/facilitator/home';
const FACILITATOR_RESOURCE_LIBRARY_ROUTE = '/facilitator/resource-library';
const MODULE_PROGRESS_KEYS = [
  'introduction',
  'strategies1',
  'strategies2',
  'hybridEZdelivery',
];
const MODULE_PATH_PREFIXES = [
  '/facilitator/introduction',
  '/facilitator/strategies-1',
  '/facilitator/strategies-2',
  '/facilitator/hybrid-ezparent-delivery',
];
const lockedNavItemClassName = 'bg-gray-300 text-black cursor-not-allowed';
const lockedSubModuleClassName = 'text-gray-500 bg-gray-200 cursor-not-allowed';

const CheckIcon = ({ className = 'h-4 w-4' }) => (
  <svg
    aria-hidden='true'
    viewBox='0 0 16 16'
    className={className}
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M3.5 8.5L6.5 11.5L12.5 4.5'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const LockIcon = ({ className = 'h-4 w-4' }) => (
  <svg
    aria-hidden='true'
    viewBox='0 0 16 16'
    className={className}
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M5 7V5.5C5 3.84315 6.34315 2.5 8 2.5C9.65685 2.5 11 3.84315 11 5.5V7'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <rect
      x='3.5'
      y='7'
      width='9'
      height='6.5'
      rx='1.5'
      stroke='currentColor'
      strokeWidth='1.5'
    />
  </svg>
);

const SpinnerIcon = ({ className = 'h-4 w-4' }) => (
  <svg
    aria-hidden='true'
    viewBox='0 0 16 16'
    className={`animate-spin motion-reduce:animate-none ${className}`}
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <circle
      cx='8'
      cy='8'
      r='6'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeDasharray='28 10'
    />
  </svg>
);

const NavLabel = ({ children, isComplete = false, className = '' }) => (
  <span
    className={`flex w-full items-center justify-between gap-3 ${className}`}
  >
    <span className='flex-1'>{children}</span>
    {isComplete ? (
      <span
        className='flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#4caf50] text-white border-white border-2'
        title='Completed'
      >
        <CheckIcon className='h-3 w-3' />
      </span>
    ) : null}
  </span>
);

const LockedNavLabel = ({ children }) => (
  <span className='flex w-full items-center justify-between gap-3'>
    <span className='flex-1'>{children}</span>
    <LockIcon className='h-4 w-4 shrink-0' />
  </span>
);

const LoadingNavLabel = ({ children }) => (
  <span className='flex w-full items-center justify-between gap-3'>
    <span className='flex-1'>{children}</span>
    <SpinnerIcon className='h-4 w-4 shrink-0' />
  </span>
);

const getFirstRouteIndexForSection = (sectionId, modulePathPrefix) => {
  const moduleRoutes = flatFacilitatorRoutes.filter((route) =>
    route.fullUrl.startsWith(modulePathPrefix),
  );
  const matchingRoutes = moduleRoutes.filter(
    (route) => route.sectionId === sectionId,
  );

  if (!matchingRoutes.length) {
    return -1;
  }

  return Math.min(
    ...matchingRoutes.map((route) =>
      moduleRoutes.findIndex(
        (candidate) => candidate.fullUrl === route.fullUrl,
      ),
    ),
  );
};

const SideNav = ({
  opened,
  isMobileNav = false,
  adminProgress = {},
  isLoadingAdminProgress = false,
}) => {
  const location = useLocation();
  const hasFullFacilitatorProgramAccess =
    hasFullFacilitatorProgramAccessRole(localStorage.getItem('role'));
  const hasCompletedTrainingGate =
    hasFullFacilitatorProgramAccess ||
    adminProgress.preSurveyCompleted ||
    hasStartedProgramFromProgress(adminProgress);
  const isTrainingUnlocked = isFacilitatorTrainingUnlockedForTesting
    ? true
    : hasCompletedTrainingGate;
  const providerValue = useAccordionProvider({
    allowMultiple: true,
    transition: true,
    transitionTimeout: 250,
  });
  const { toggle } = providerValue;

  const farthestProgressRouteIndex = useMemo(() => {
    return MODULE_PROGRESS_KEYS.reduce(
      (highestIndex, moduleProgressKey, moduleIndex) => {
        const moduleProgress = adminProgress?.[moduleProgressKey];

        if (!moduleProgress || moduleProgress.locked) {
          return highestIndex;
        }

        const moduleRoutes = flatFacilitatorRoutes.filter((route) =>
          route.fullUrl.startsWith(MODULE_PATH_PREFIXES[moduleIndex]),
        );

        if (!moduleRoutes.length) {
          return highestIndex;
        }

        const safeModuleRouteIndex = Math.min(
          Math.max(moduleProgress.currentIndex, 0),
          moduleRoutes.length - 1,
        );
        const farthestModuleRoute = moduleRoutes[safeModuleRouteIndex];
        const farthestFlatRouteIndex = flatFacilitatorRoutes.findIndex(
          (route) => route.fullUrl === farthestModuleRoute.fullUrl,
        );

        return Math.max(highestIndex, farthestFlatRouteIndex);
      },
      -1,
    );
  }, [adminProgress]);

  const isGroupComplete = (matcher) => {
    const matchingRoutes = flatFacilitatorRoutes.filter(matcher);

    if (!matchingRoutes.length) {
      return false;
    }

    const lastMatchingIndex = Math.max(
      ...matchingRoutes.map((route) =>
        flatFacilitatorRoutes.findIndex(
          (candidate) => candidate.fullUrl === route.fullUrl,
        ),
      ),
    );

    return farthestProgressRouteIndex > lastMatchingIndex;
  };

  useEffect(() => {
    const currentPath = location.pathname;
    const activeModule = fascilitatorTrainingRoutes.find((mod) => {
      return (
        currentPath === mod.fullUrl || currentPath.startsWith(`${mod.fullUrl}/`)
      );
    });

    fascilitatorTrainingRoutes.forEach((mod) => {
      if (!activeModule) {
        toggle(mod.id, false);
      } else {
        if (mod.id === activeModule.id) {
          toggle(mod.id, true);
        } else {
          toggle(mod.id, false);
        }
      }
    });
  }, [location.pathname, toggle]);

  return (
    <Navbar
      hidden={!opened}
      hiddenBreakpoint={FACILITATOR_NAV_BREAKPOINT}
      width={{ base: 300 }}
      style={{ zIndex: 3100 }}
      className='bg-dark-blue text-white border-none overflow-y-auto overflow-x-hidden'
    >
      <div className='flex flex-col gap-[2px] items-stretch'>
        {isMobileNav && (
          <div className='bg-white p-4'>
            <FacilitatorHeaderActions isMobile />
          </div>
        )}
        <Link
          key={'home'}
          to={FACILITATOR_HOME_ROUTE}
          className={`${modStyles} ${
            location.pathname === FACILITATOR_HOME_ROUTE
              ? 'bg-purple'
              : 'bg-light-blue'
          }`}
        >
          <NavLabel>Home</NavLabel>
        </Link>
        <Link
          key={'resource-library'}
          to={FACILITATOR_RESOURCE_LIBRARY_ROUTE}
          className={`${modStyles} ${
            location.pathname === FACILITATOR_RESOURCE_LIBRARY_ROUTE
              ? 'bg-purple'
              : 'bg-light-blue'
          }`}
        >
          <NavLabel>Resource Library</NavLabel>
        </Link>
        <ControlledAccordion
          providerValue={providerValue}
          className='flex w-full flex-col gap-[2px]'
        >
          {fascilitatorTrainingRoutes.map((mod, moduleIndex) => {
            if (isLoadingAdminProgress) {
              return (
                <span
                  key={mod.id}
                  className={`${modStyles} ${lockedNavItemClassName}`}
                  aria-busy='true'
                  aria-label={`${mod.title}, loading progress`}
                  title='Loading training progress.'
                >
                  <LoadingNavLabel>{mod.title}</LoadingNavLabel>
                </span>
              );
            }

            const moduleProgressKey = MODULE_PROGRESS_KEYS[moduleIndex];
            const modulePathPrefix = MODULE_PATH_PREFIXES[moduleIndex];
            const isCurrentModule =
              location.pathname.startsWith(modulePathPrefix);
            const moduleProgressIndex =
              adminProgress?.[moduleProgressKey]?.currentIndex ?? -1;
            const isModuleLockedByProgress =
              !hasFullFacilitatorProgramAccess &&
              adminProgress?.[moduleProgressKey]?.locked !== false &&
              !isCurrentModule;
            const isLocked = !isTrainingUnlocked || isModuleLockedByProgress;
            const lockedTitle = isTrainingUnlocked
              ? 'Complete the previous module to unlock this module.'
              : 'Watch the facilitator training home page video to unlock training.';
            const isModuleComplete = hasFullFacilitatorProgramAccess
              ? true
              : moduleProgressKey === 'hybridEZdelivery'
                ? isAdminProgramCompleteFromProgress(adminProgress)
                : isGroupComplete((route) => route.moduleId === mod.id);

            if (mod.subRoutes) {
              if (isLocked) {
                return (
                  <span
                    key={mod.id}
                    className={`${modStyles} ${lockedNavItemClassName}`}
                    title={lockedTitle}
                  >
                    <LockedNavLabel>{mod.title}</LockedNavLabel>
                  </span>
                );
              }

              return (
                <NavLink
                  key={mod.id}
                  to={mod.fullUrl}
                  className={(isActive) =>
                    `${modStyles} ` +
                    (!isActive ? ' bg-light-blue' : 'bg-purple ')
                  }
                >
                  <NavLabel isComplete={isModuleComplete}>{mod.title}</NavLabel>
                </NavLink>
              );
            }
            if (mod.sections) {
              return (
                <Item
                  key={mod.id}
                  itemKey={mod.id}
                  initialEntered={isCurrentModule}
                  disabled={isLocked}
                  className={(isActive) =>
                    '' + (!isActive ? ' bg-light-blue' : 'bg-purple ')
                  }
                  header={() =>
                    isLocked ? (
                      <span
                        className={`${modStyles} ${lockedNavItemClassName}`}
                        title={lockedTitle}
                      >
                        <LockedNavLabel>{mod.title}</LockedNavLabel>
                      </span>
                    ) : (
                      <Link
                        className={`${modStyles} bg-light-blue text-white`}
                        to={mod.sections[0].fullUrl}
                      >
                        <NavLabel isComplete={isModuleComplete}>
                          {mod.title}
                        </NavLabel>
                      </Link>
                    )
                  }
                >
                  <div
                    className={`flex flex-col gap-[2px] py-[2px] ${
                      isLocked ? 'bg-gray-200' : 'bg-black'
                    }`}
                  >
                    {mod.sections.map((section) => {
                      const sectionPath = section.fullUrl;
                      if (
                        hasFullFacilitatorProgramAccess &&
                        sectionPath === FACILITATOR_CERTIFICATE_ROUTE
                      ) {
                        return null;
                      }
                      const sectionFirstRouteIndex =
                        getFirstRouteIndexForSection(
                          section.id,
                          modulePathPrefix,
                        );
                      const isCurrentSection =
                        location.pathname.startsWith(sectionPath);
                      const isSectionLockedByProgress =
                        !hasFullFacilitatorProgramAccess &&
                        !isCurrentSection &&
                        sectionFirstRouteIndex > moduleProgressIndex;
                      if (isLocked || isSectionLockedByProgress) {
                        return (
                          <span
                            key={section.url}
                            className={`${modStyles} ${lockedSubModuleClassName}`}
                            title={
                              isLocked
                                ? lockedTitle
                                : 'Finish the current submodule to unlock this one.'
                            }
                          >
                            {section.title}
                          </span>
                        );
                      }

                      return (
                        <NavLink
                          key={section.url}
                          to={sectionPath}
                          className={(isActive) =>
                            `${modStyles} ` +
                            (!isActive
                              ? ' bg-white text-black'
                              : 'bg-dark-blue text-white')
                          }
                        >
                          <NavLabel className='w-full'>
                            {section.title}
                          </NavLabel>
                        </NavLink>
                      );
                    })}
                  </div>
                </Item>
              );
            }
            return null;
          })}
        </ControlledAccordion>
      </div>
    </Navbar>
  );
};

export default SideNav;
